import React from 'react';
import { LayerNameInput } from './LayerNameInput';
import { LayerColorPicker } from './LayerColorPicker';
import { LayerTextColorToggle } from './LayerTextColorToggle';
import { LayerFormatPreview } from './LayerFormatPreview';
import type { WizardLayer } from '@services/adminConsoleService';

interface LayerSecurityGroup {
  SecurityGroupID: number;
  Name: string;
  GroupName: string;
}

interface LayerRowProps {
  layer: WizardLayer;
  onChange: (
    layerId: number,
    field: 'Name' | 'Color' | 'TextColor' | 'IsEnable',
    value: string | boolean
  ) => void;
  isNew?: boolean;
  autoEdit?: boolean;
  securityGroups?: LayerSecurityGroup[];
  onEditSecurityGroups?: () => void;
}

const PREVIEW_LIMIT = 5;

// System layers have a real positive ID of 0 or 1. New unsaved layers use negative IDs and are always editable.
const isSystemLayer = (layerId: number) => layerId > 0 && layerId <= 1;

export const LayerRow: React.FC<LayerRowProps> = ({
  layer,
  onChange,
  isNew = false,
  autoEdit = false,
  securityGroups = [],
  onEditSecurityGroups,
}) =>
{
  const disabled = isSystemLayer(layer.LayerId);
  const isUnsaved = layer.LayerId < 0;

  const preview = securityGroups.slice(0, PREVIEW_LIMIT);
  const overflow = securityGroups.length - PREVIEW_LIMIT;

  return (
    <div
      className={`border-b transition-colors ${
        isNew ? 'border-[#62aacb] dark:bg-[#4a4c4c]' : 'border-[#eee] bg-white hover:bg-[#fafafa]'
      }`}
      style={{ borderLeft: `4px solid ${layer.Color}` }}
    >
      {/* ── Main row ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Checkbox */}
        <button
          role="checkbox"
          aria-checked={layer.IsEnable}
          disabled={disabled}
          onClick={() => onChange(layer.LayerId, 'IsEnable', !layer.IsEnable)}
          title={
            disabled
              ? 'System layer — cannot be modified'
              : layer.IsEnable
                ? 'Disable layer'
                : 'Enable layer'
          }
          className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center transition-colors disabled:opacity-40
    ${layer.IsEnable ? 'bg-white border-[#62aacb]' : 'bg-white border-[#ccc]'}`}
        >
          {layer.IsEnable && (
            <span
              className="material-icons text-[#62aacb]"
              style={{ fontSize: '18px', fontWeight: 'bolder', lineHeight: 1 }}
            >
              check
            </span>
          )}
        </button>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <LayerNameInput
            name={layer.Name}
            onSave={v => onChange(layer.LayerId, 'Name', v)}
            disabled={disabled}
            autoEdit={autoEdit}
          />
          {isNew && (
            <span className="text-[10px] text-[#62aacb] block mt-0.5">
              Edit name · click color swatch to change color
            </span>
          )}
        </div>

        {/* Layer Color label + picker */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            className={`text-[11px] hidden sm:inline ${isNew ? 'text-[#62aacb] font-medium' : 'text-[#777]'}`}
          >
            Layer Color:
          </span>
          <LayerColorPicker
            color={layer.Color}
            onSelect={c => onChange(layer.LayerId, 'Color', c)}
            disabled={disabled}
          />
        </div>

        {/* Text Color label + toggle */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            className={`text-[11px] hidden sm:inline ${isNew ? 'text-[#62aacb] font-medium' : 'text-[#777]'}`}
          >
            Text Color:
          </span>
          <LayerTextColorToggle
            textColor={layer.TextColor}
            onChange={v => onChange(layer.LayerId, 'TextColor', v)}
            disabled={disabled}
          />
        </div>

        {/* Preview */}
        <div className="flex-shrink-0 w-[130px] hidden md:flex justify-end">
          <LayerFormatPreview name={layer.Name} color={layer.Color} textColor={layer.TextColor} />
        </div>
      </div>

      {/* ── Security Groups row ── */}
      {!isUnsaved && (
        <div className="flex items-start gap-1 px-4 pb-2.5 text-[11px]">
          <span className="text-[#aaa] flex-shrink-0">Security Groups:</span>
          {securityGroups.length === 0 ? (
            <span className="text-[#ccc] italic">None</span>
          ) : (
            <span className="text-[#555] flex-wrap">
              {preview.map((sg, i) => (
                <span key={sg.SecurityGroupID}>
                  <span className="text-[#777]">{sg.GroupName}:</span> <span>{sg.Name}</span>
                  {i < preview.length - 1 || overflow > 0 ? ', ' : ''}
                </span>
              ))}
              {overflow > 0 && <span className="text-[#888] font-medium">+{overflow}</span>}
            </span>
          )}
          {onEditSecurityGroups && (
            <button
              onClick={onEditSecurityGroups}
              className="ml-1.5 text-[#62aacb] hover:underline flex-shrink-0"
            >
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
};
